import { DEFAULT_STALE_TIME } from '@/lib/constants'

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  return String(error)
}

// TODO: Integrate with error reporting service (e.g., Sentry) when ready
export function reportError({ message }: { message: string }) {
  console.error('Error reported:', message)
}

export const logErrorToService = (
  error: Error,
  errorInfo?: { componentStack?: string | null },
  additionalContext?: Record<string, any>
) => {
  const timestamp = new Date().toISOString()

  const errorData = {
    timestamp,
    message: error.message,
    name: error.name,
    stack: error.stack,
    componentStack: errorInfo?.componentStack,
    ...additionalContext,
  }

  if (process.env.NODE_ENV !== 'production') {
    console.group(`🚨 Error Boundary [${timestamp}]`)
    console.error('Error:', error)
    console.error('Error Info:', errorInfo)

    if (additionalContext) {
      console.error('Context:', additionalContext)
    }
    console.groupEnd()
  }

  // TODO: In production, send to error monitoring service
  // Example: Sentry.captureException(error, { contexts: { react: errorInfo, custom: additionalContext } })

  return errorData
}

export const createErrorBoundaryProps = (
  FallbackComponent: React.ComponentType<any>,
  additionalContext?: Record<string, any>
) => ({
  FallbackComponent,
  onError: (error: Error, errorInfo: { componentStack?: string | null }) => {
    logErrorToService(error, errorInfo, additionalContext)
  },
  onReset: () => {
    // Clear any error state if needed
    if (additionalContext?.app) {
      console.info(`♻️ ErrorBoundary reset for app: ${additionalContext.app}`)
    }
  },
})

export const createQueryClientConfig = (options?: {
  staleTime?: number
  retry?: number
  onQueryError?: (error: unknown) => void
  onMutationError?: (error: unknown) => void
}) => {
  const {
    staleTime = DEFAULT_STALE_TIME,
    retry = 3,
    onQueryError,
    onMutationError,
  } = options || {}

  return {
    defaultOptions: {
      queries: {
        staleTime,
        retry,
        refetchOnWindowFocus: false,
        onError: (error: unknown) => {
          console.error('❌ Query Error:', error)
          onQueryError?.(error)
        },
      },
      mutations: {
        retry: 1,
        onError: (error: unknown) => {
          console.error('❌ Mutation Error:', error)
          onMutationError?.(error)
        },
      },
    },
  }
}
